import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Film, 
  ArrowLeft, 
  User, 
  Mail, 
  Crown, 
  LogOut, 
  AlertTriangle,
  X,
  Gift,
  Sparkles,
  Heart,
  CheckCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Settings = () => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelStep, setCancelStep] = useState(1); // 1 = retention offer, 2 = confirm cancel
  const [cancelling, setCancelling] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const token = localStorage.getItem('moviebot_token');
      const response = await fetch(`${API_URL}/api/subscription/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Subscription cancelled. You can still use your plan until the end of the billing period.');
        setShowCancelModal(false);
        setCancelStep(1);
        // Refresh user data
        if (refreshUser) {
          await refreshUser();
        }
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to cancel subscription');
      }
    } catch (error) {
      toast.error('Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const openCancelModal = () => {
    setCancelStep(1);
    setShowCancelModal(true);
  };

  const isPaidUser = user?.subscription_tier && user.subscription_tier !== 'free';

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors" data-testid="settings-back">
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
          <div className="flex items-center gap-2">
            <Film className="w-6 h-6 text-primary" />
            <span className="font-bold tracking-tight text-white">Settings</span>
          </div>
        </div>
      </header>

      <div className="pt-24 pb-20 px-6">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-3xl font-bold tracking-tight text-white mb-2">Account Settings</h1>
            <p className="text-muted-foreground mb-10">Manage your profile and preferences</p>

            {/* Profile Section */}
            <div className="space-y-6">
              <div className="p-6 bg-secondary/30 border border-white/5">
                <h2 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                  <User className="w-5 h-5 text-primary" />
                  Profile Information
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-muted-foreground text-sm">Name</Label>
                    <Input
                      value={user?.name || ''}
                      disabled
                      className="mt-2 bg-black/50 border-white/10 text-white h-12"
                      data-testid="settings-name"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-muted-foreground text-sm">Email</Label>
                    <div className="relative mt-2">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        value={user?.email || ''}
                        disabled
                        className="bg-black/50 border-white/10 text-white h-12 pl-10"
                        data-testid="settings-email"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Subscription Section */}
              <div className="p-6 bg-secondary/30 border border-white/5">
                <h2 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                  <Crown className="w-5 h-5 text-primary" />
                  Subscription
                </h2>
                
                <div className="flex items-center justify-between p-4 bg-black/30 border border-white/5 mb-4">
                  <div>
                    <p className="text-white font-medium">Current Plan</p>
                    <p className="text-sm text-muted-foreground capitalize">{user?.subscription_tier || 'Free'}</p>
                  </div>
                  {isPaidUser && (
                    <span className="px-3 py-1 bg-primary/20 text-primary text-xs font-bold uppercase">
                      Active
                    </span>
                  )}
                </div>

                <div className="space-y-3">
                  <Link to="/subscription">
                    <Button 
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-widest text-xs py-4"
                      data-testid="manage-subscription-btn"
                    >
                      {!isPaidUser ? 'Upgrade Plan' : 'Change Plan'}
                    </Button>
                  </Link>

                  {isPaidUser && (
                    <Button
                      onClick={openCancelModal}
                      variant="ghost"
                      className="w-full text-muted-foreground hover:text-red-400 hover:bg-red-500/10 text-xs"
                      data-testid="cancel-subscription-btn"
                    >
                      Cancel Subscription
                    </Button>
                  )}
                </div>
              </div>

              {/* Danger Zone */}
              <div className="p-6 bg-red-500/5 border border-red-500/20">
                <h2 className="text-lg font-bold text-white mb-4">Danger Zone</h2>
                <p className="text-muted-foreground text-sm mb-4">
                  Sign out of your account. You can always sign back in.
                </p>
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                  data-testid="logout-settings-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Cancel Subscription Modal */}
      <AnimatePresence>
        {showCancelModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowCancelModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-secondary border border-white/10 max-w-md w-full overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {cancelStep === 1 ? (
                /* Step 1: Retention Offer */
                <>
                  <div className="p-6 text-center">
                    <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Heart className="w-8 h-8 text-primary" />
                    </div>
                    
                    <h2 className="text-2xl font-bold text-white mb-2">We'd hate to see you go!</h2>
                    <p className="text-muted-foreground mb-6">
                      Before you cancel, consider what you'll be missing out on:
                    </p>

                    <div className="space-y-3 text-left mb-6">
                      <div className="flex items-start gap-3 p-3 bg-black/30 rounded-lg">
                        <Sparkles className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-white font-medium text-sm">Unlimited AI Scene Generation</p>
                          <p className="text-xs text-muted-foreground">Keep creating without limits</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3 p-3 bg-black/30 rounded-lg">
                        <Film className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-white font-medium text-sm">Access to All 3 Acts</p>
                          <p className="text-xs text-muted-foreground">Complete your full screenplay</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3 p-3 bg-black/30 rounded-lg">
                        <Gift className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-white font-medium text-sm">Your Story Progress</p>
                          <p className="text-xs text-muted-foreground">Don't lose momentum on your script</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-primary/20 to-green-500/20 border border-primary/30 p-4 rounded-lg mb-6">
                      <p className="text-white font-bold mb-1">Stay and get 20% off next month!</p>
                      <p className="text-xs text-muted-foreground">
                        We value your creativity. Keep your subscription and we'll apply a discount to your next billing cycle.
                      </p>
                    </div>
                  </div>

                  <div className="p-4 border-t border-white/10 bg-black/20 flex gap-3">
                    <Button
                      className="flex-1 bg-primary text-black hover:bg-primary/90 font-bold"
                      onClick={() => setShowCancelModal(false)}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Keep My Subscription
                    </Button>
                    <Button
                      variant="ghost"
                      className="text-muted-foreground hover:text-white"
                      onClick={() => setCancelStep(2)}
                    >
                      Continue
                    </Button>
                  </div>
                </>
              ) : (
                /* Step 2: Confirm Cancellation */
                <>
                  <div className="p-6">
                    <button
                      onClick={() => setShowCancelModal(false)}
                      className="absolute top-4 right-4 text-muted-foreground hover:text-white"
                    >
                      <X className="w-5 h-5" />
                    </button>

                    <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center mb-4">
                      <AlertTriangle className="w-6 h-6 text-red-400" />
                    </div>
                    
                    <h2 className="text-xl font-bold text-white mb-2">Confirm Cancellation</h2>
                    <p className="text-muted-foreground mb-6">
                      Are you sure you want to cancel your <span className="text-white font-medium capitalize">{user?.subscription_tier}</span> subscription?
                    </p>

                    <div className="bg-black/30 border border-white/10 p-4 rounded-lg mb-6">
                      <p className="text-sm text-muted-foreground">
                        <span className="text-white font-medium">What happens next:</span>
                      </p>
                      <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                        <li>• You'll keep access until the end of your billing period</li>
                        <li>• After that, you'll be downgraded to the Free plan</li>
                        <li>• You'll only have access to Act 1 (Setup)</li>
                        <li>• Your projects and progress will be saved</li>
                      </ul>
                    </div>
                  </div>

                  <div className="p-4 border-t border-white/10 bg-black/20 flex gap-3">
                    <Button
                      variant="outline"
                      className="flex-1 border-white/20"
                      onClick={() => setCancelStep(1)}
                    >
                      Go Back
                    </Button>
                    <Button
                      className="flex-1 bg-red-500 text-white hover:bg-red-600 font-bold"
                      onClick={handleCancelSubscription}
                      disabled={cancelling}
                      data-testid="confirm-cancel-btn"
                    >
                      {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
                    </Button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Settings;
